import { concat, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { and, eq, or } from 'ember-truth-helpers';
import InvitesTableInviteListNameCell from 'garaje/components/invites/table/invite-list/name-cell';
import InvitesTableInviteListPlainCell from 'garaje/components/invites/table/invite-list/plain-cell';
import MultiCheckbox from 'garaje/components/multi-checkbox';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import type FlowModel from 'garaje/models/flow';
import type InviteModel from 'garaje/models/invite';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import type { RecordArray } from 'garaje/utils/type-utils';
import { filterBy, intersect, reads } from 'macro-decorators';
import { localCopy } from 'tracked-toolbox';

interface InviteColumnConfig {
  name: string;
  type: string | null;
  label?: string;
}

interface InvitesTableInviteListSignature {
  Args: {
    flow?: FlowModel;
    invites?: RecordArray<InviteModel> | InviteModel[] | null;
    isLoading?: boolean;
    enableSelection?: boolean;
    onSelection?: (selectedInvites: InviteModel[]) => void;
    selectedInvites?: InviteModel[];
    disabled?: boolean;
  };
  Blocks: {
    'empty-message': [];
    'list-header': [{ invites: InviteModel[]; selection: InviteModel[] }];
    name: [{ column: InviteColumnConfig; invite: InviteModel; isSelected: boolean }];
    'bulk-action': [{ invites: InviteModel[]; selection: InviteModel[] }];
    email: [{ column: InviteColumnConfig; invite: InviteModel; isSelected: boolean }];
    phone: [{ column: InviteColumnConfig; invite: InviteModel; isSelected: boolean }];
    'private-notes': [{ column: InviteColumnConfig; invite: InviteModel; isSelected: boolean }];
    'custom-column': [{ column: InviteColumnConfig; invite: InviteModel; isSelected: boolean }];
    'row-action': [{ invite: InviteModel; isSelected: boolean }];
  };
  Element: HTMLDivElement;
}

export default class InvitesTableInviteList extends Component<InvitesTableInviteListSignature> {
  scrollingElementId: string = `${guidFor(this)}-scrolling-list`;

  @localCopy('args.selectedInvites', () => []) selectedInvites!: InviteModel[];

  @intersect('invites', 'selectedInvites') selectedOnCurrentPage!: InviteModel[];
  @reads('args.flow') flow!: FlowModel;
  @reads('args.invites', []) invites!: InviteModel[];
  @reads('invites.length', 0) pageLength!: number;
  @filterBy('columns', 'type') tableColumns!: InviteColumnConfig[];

  get sortedSignInFields(): SignInFieldModel[] {
    return (
      this.flow.signInFieldPage.content?.signInFields.content
        .filter((f) => f.isTopLevel !== false)
        .toSorted((a, b) => a.position - b.position) ?? []
    );
  }

  get columns(): InviteColumnConfig[] {
    const primaryColumns = [
      { name: 'Name', type: 'name' },
      { name: 'Visitor email', type: 'email' },
    ];
    const standardColumns = [{ name: 'Private notes', type: 'private_notes' }];

    return [...primaryColumns, ...this.flowColumns, ...standardColumns];
  }

  get flowColumns(): InviteColumnConfig[] {
    if (!this.flow) return [];

    return this.sortedSignInFields.map((field: SignInFieldModel): InviteColumnConfig => {
      return this.columnConfigForField(field);
    });
  }

  get isPageSelected(): boolean {
    const { pageLength, selectedOnCurrentPage } = this;

    if (!(pageLength > 0 && selectedOnCurrentPage.length > 0)) return false;

    return selectedOnCurrentPage.length === pageLength;
  }

  get isPagePartiallySelected(): boolean {
    const { isPageSelected, selectedOnCurrentPage } = this;

    if (isPageSelected) return false;

    return selectedOnCurrentPage.length > 0;
  }

  columnConfigForField(field: SignInFieldModel): InviteColumnConfig {
    // Set const individually to avoid "Unsafe array destructuring of a
    // tuple element with an `any` value" lint error
    const name = field?.name ?? '';
    const isPhone = field?.isPhone ?? false;
    const isHost = field?.isHost ?? false;
    const isCustom = field?.isCustom ?? false;

    if (isPhone) return { name: 'Your Phone Number', label: 'Phone number', type: 'phone' };
    if (isHost) return { name: '', type: null };
    if (!isCustom) return { name: '', type: null };

    return { name, type: 'custom' };
  }

  notifySelectionChange(selected: InviteModel[] = this.selectedInvites): void {
    this.args.onSelection?.(selected);
  }

  @action
  selectionDidChange(selected: InviteModel[]): void {
    this.selectedInvites = [...selected];
    this.notifySelectionChange();
  }

  @action
  resetSelection(): void {
    this.selectedInvites = [];
    this.notifySelectionChange();
  }

  @action
  selectAllInvites(value: boolean): void {
    const records = this.invites.slice();

    if (value) {
      const newSelection = [...this.selectedInvites, ...records].filter(
        (value: InviteModel, index: number, self: InviteModel[]): boolean => {
          return self.indexOf(value) === index;
        },
      );

      this.selectedInvites = newSelection;
    } else {
      const newSelection = this.selectedInvites.filter((invite: InviteModel): boolean => !records.includes(invite));

      this.selectedInvites = newSelection;
    }

    this.notifySelectionChange();
  }

  @action
  handleInvitesUpdate(): void {
    // Scroll the listing back to the top when @invites updated
    const { scrollingElementId } = this;
    const scrollingElement = document?.getElementById(scrollingElementId);

    if (scrollingElement) scrollingElement.scrollTo(0, 0);
  }

  <template>
    <div aria-label="Invites List" ...attributes {{didUpdate this.handleInvitesUpdate @invites}} data-test-invites-list>
      {{#if (has-block "list-header")}}
        {{yield (hash invites=this.invites selection=this.selectedInvites) to="list-header"}}
      {{/if}}

      <div
        id={{this.scrollingElementId}}
        class="relative max-h-[48rem] w-full border border-carbon-10 rounded-md overflow-auto overscroll-contain"
      >
        {{#if @isLoading}}
          <div class="z-50 absolute inset-0 bg-white/50" data-test-task-spinner>
            <LoadingSpinnerPulse />
          </div>
        {{/if}}
        <div class="w-fit min-w-full">
          <table class="table w-fit min-w-full text-sm border-spacing-0">
            <thead class="{{if (eq this.pageLength 0) 'hidden'}}">
              <tr>
                {{#if @enableSelection}}
                  <th
                    class="h-10 w-2 min-w-2 px-2 text-carbon-50 font-bold text-center align-middle bg-white border-b border-carbon-10 sticky top-0 left-0 z-40"
                    scope="col"
                    data-test-invites-list-heading-select-all
                  >
                    <input
                      data-test-invites-list-select-all
                      type="checkbox"
                      {{on "click" (pick "target.checked" this.selectAllInvites)}}
                      checked={{this.isPageSelected}}
                      {{! @glint-expect-error - Not an attribute that can be set directly. Check if this works }}
                      indeterminate={{this.isPagePartiallySelected}}
                      disabled={{@disabled}}
                    />
                  </th>
                {{/if}}

                {{#if (and this.selectedInvites.length (has-block "bulk-action"))}}
                  <td
                    class="h-10 text-carbon-50 font-bold align-middle bg-white border-b border-carbon-10 sticky top-0 z-20"
                    colspan={{this.tableColumns.length}}
                  >
                    &nbsp;
                  </td>
                {{else}}
                  {{#each this.tableColumns as |column|}}
                    {{#let
                      (concat
                        "w-48 min-w-[12rem] border-r bg-white sticky z-20 " (if @enableSelection "left-9" "left-0")
                      )
                      as |nameHeaderClass|
                    }}
                      <th
                        class="h-10 text-carbon-50 font-bold align-middle bg-white border-b border-carbon-10 sticky top-0 z-10 truncate
                          {{if (eq column.type 'name') nameHeaderClass 'min-w-[12rem] max-w-[12rem]'}}"
                        scope="col"
                        data-test-invites-list-heading
                        data-test-invites-list-heading-for={{column.name}}
                      >
                        {{or column.label column.name}}
                      </th>
                    {{/let}}
                  {{/each}}
                {{/if}}

                {{#let
                  (concat
                    "h-10 text-carbon-50 font-bold align-middle bg-white border-b border-carbon-10 sticky top-0 "
                    (if (has-block "row-action") "border-l z-30 right-0" "w-px max-w-px px-0 z-10")
                    (if (and this.selectedInvites.length (has-block "bulk-action")) "!border-l-0 right-0 z-30")
                  )
                  as |actionHeaderClass|
                }}
                  <th class={{actionHeaderClass}} scope="col" data-test-invites-list-heading-action>
                    {{#if (and this.selectedInvites.length (has-block "bulk-action"))}}
                      {{yield (hash invites=this.invites selection=this.selectedInvites) to="bulk-action"}}
                    {{/if}}
                  </th>
                {{/let}}
              </tr>
            </thead>
            <tbody>
              {{#if this.invites.length}}
                <MultiCheckbox
                  @selectedDidChange={{this.selectionDidChange}}
                  @onDestroy={{this.resetSelection}}
                  @selectedItems={{this.selectedInvites}}
                  @items={{this.invites}}
                  as |invite checkbox isSelected|
                >
                  <tr
                    {{! @glint-expect-error: ember data types cause issues here }}
                    class="{{if isSelected 'selected'}} {{if invite.isDeleted 'deleted'}}"
                    data-test-invites-list-row-for={{invite.id}}
                  >
                    {{#if @enableSelection}}
                      {{#let (component (ensureSafeComponent checkbox.checkbox this)) as |CheckBox|}}
                        <td
                          class="h-16 w-2 min-w-2 px-2 text-center align-middle bg-white border-b border-carbon-10 sticky left-0 z-10 !pointer-events-auto
                            {{if isSelected 'selected'}}"
                        >
                          <div class="!opacity-100" data-test-invites-list-select={{invite.id}}>
                            <CheckBox @isDisabled={{@disabled}} @item={{invite}} class="mx-1" />
                          </div>
                        </td>
                      {{/let}}
                    {{/if}}
                    {{#each this.tableColumns as |column|}}
                      {{#if (eq column.type "name")}}
                        {{#if (has-block "name")}}
                          {{yield (hash column=column invite=invite isSelected=isSelected) to="name"}}
                        {{else}}
                          {{#let
                            (concat
                              "h-16 w-48 min-w-[12rem] align-middle bg-white border-b border-r border-carbon-10 sticky z-10 "
                              (if @enableSelection "left-9" "left-0")
                              (if isSelected " selected" "")
                            )
                            as |nameCellClass|
                          }}
                            <InvitesTableInviteListNameCell
                              class={{nameCellClass}}
                              @model={{invite}}
                              scope="row"
                              data-test-invites-list-content-id={{invite.id}}
                              data-test-invites-list-content-for={{column.name}}
                            />
                          {{/let}}
                        {{/if}}
                      {{else if (eq column.type "email")}}
                        {{#if (has-block "email")}}
                          {{yield (hash column=column invite=invite isSelected=isSelected) to="email"}}
                        {{else}}
                          <InvitesTableInviteListPlainCell
                            class="h-16 min-w-[12rem] max-w-[12rem] align-middle border-b border-carbon-10 break-all
                              {{if isSelected 'selected'}}"
                            @text={{invite.email}}
                            @lines={{1}}
                            data-test-invites-list-content-id={{invite.id}}
                            data-test-invites-list-content-for={{column.name}}
                          />
                        {{/if}}
                      {{else if (eq column.type "phone")}}
                        {{#if (has-block "phone")}}
                          {{yield (hash column=column invite=invite isSelected=isSelected) to="phone"}}
                        {{else}}
                          <InvitesTableInviteListPlainCell
                            class="h-16 min-w-[12rem] max-w-[12rem] align-middle border-b border-carbon-10 break-all
                              {{if isSelected 'selected'}}"
                            @content={{invite}}
                            @name={{column.name}}
                            @lines={{1}}
                            data-test-invites-list-content-id={{invite.id}}
                            data-test-invites-list-content-for={{column.name}}
                          />
                        {{/if}}
                      {{else if (eq column.type "private_notes")}}
                        {{#if (has-block "private-notes")}}
                          {{yield (hash column=column invite=invite isSelected=isSelected) to="private-notes"}}
                        {{else}}
                          <InvitesTableInviteListPlainCell
                            class="h-16 min-w-[12rem] max-w-[12rem] align-middle border-b border-carbon-10 text-sm italic
                              {{if isSelected 'selected'}}"
                            @text={{invite.privateNotes}}
                            @lines={{2}}
                            data-test-invites-list-content-id={{invite.id}}
                            data-test-invites-list-content-for={{column.name}}
                          />
                        {{/if}}
                      {{else if column.type}}
                        {{#if (has-block "custom-column")}}
                          {{yield (hash column=column invite=invite isSelected=isSelected) to="custom-column"}}
                        {{else}}
                          <InvitesTableInviteListPlainCell
                            class="h-16 min-w-[12rem] max-w-[12rem] align-middle border-b border-carbon-10
                              {{if isSelected 'selected'}}"
                            @content={{invite}}
                            @name={{column.name}}
                            @lines={{2}}
                            data-test-invites-list-content-id={{invite.id}}
                            data-test-invites-list-content-for={{column.name}}
                          />
                        {{/if}}
                      {{/if}}
                    {{/each}}
                    {{#if (has-block "row-action")}}
                      {{yield (hash invite=invite isSelected=isSelected) to="row-action"}}
                    {{else}}
                      <td class="h-16 w-px max-w-px px-0 border-b border-carbon-10 sticky right-0 pointer-events-none">
                        &nbsp;
                      </td>
                    {{/if}}
                  </tr>
                </MultiCheckbox>
              {{else if (eq this.pageLength 0)}}
                <tr>
                  {{#if (has-block "empty-message")}}
                    {{yield to="empty-message"}}
                  {{else}}
                    <td colspan="9999" class="text-center align-middle p-4" data-test-invites-list-empty>
                      {{#if @isLoading}}
                        <h4 class="pt-40 pb-4">
                          Loading&hellip;
                        </h4>
                      {{else}}
                        <img
                          src="/assets/images/empty-state/no-invites.png"
                          alt="No invites"
                          class="empty-invites h-48"
                        />
                        <h3 class="my-2">
                          No invites.
                        </h3>
                      {{/if}}
                    </td>
                  {{/if}}
                </tr>
              {{/if}}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </template>
}
